import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { GoogleMap, useJsApiLoader, Marker, OverlayView } from '@react-google-maps/api';
import { MapPin, Navigation, ExternalLink, Search } from 'lucide-react';
import Container from '../components/common/Container';
import { getAllProjects } from '../redux/slice/project.slice';
import { useNavigate } from 'react-router-dom';

const MAP_CONTAINER_STYLE = { width: '100%', height: '100%', borderRadius: '1.5rem' };
const DEFAULT_CENTER = { lat: 21.1702, lng: 72.8311 }; // Surat coordinates

// Custom map styles for dark theme to match the app
// Default map styles will be used for light theme

// Modern Pin Icon (Teardrop shape with center hole)
const CUSTOM_MARKER_ICON = {
    path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
    fillColor: "#d59563", // Gold
    fillOpacity: 1,
    strokeWeight: 1.5,
    strokeColor: "#ffffff",
    scale: 1.8,
    anchor: { x: 12, y: 22 },
    labelOrigin: { x: 12, y: -10 }
};

const HOVERED_MARKER_ICON = {
    ...CUSTOM_MARKER_ICON,
    scale: 2.2,
    fillColor: "#ffffff",
    strokeColor: "#d59563",
    strokeWeight: 2.5
};

const LocationPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { projects, loading } = useSelector((state) => state.project);
    const [selectedProject, setSelectedProject] = useState(null);
    const [map, setMap] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Popup State
    const [hoveredProject, setHoveredProject] = useState(null);
    const hoverTimeoutRef = useRef(null);

    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
    });

    useEffect(() => {
        if (projects.length === 0) {
            dispatch(getAllProjects());
        }
    }, [dispatch]);

    // Filter projects with valid location data
    const validProjects = useMemo(() => {
        return projects
            .filter(p => p.location?.lat && p.location?.lng)
            .filter(p => p.title.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [projects, searchTerm]);

    const handleProjectClick = (project) => {
        setSelectedProject(project);
        if (map && project.location) {
            map.panTo({ lat: project.location.lat, lng: project.location.lng });
            map.setZoom(15);
        }
    };

    const onLoad = React.useCallback(function callback(map) {
        setMap(map);
    }, []);

    const onUnmount = React.useCallback(function callback(map) {
        setMap(null);
    }, []);

    // Hover handlers with delay
    const handleMarkerEnter = (project) => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        setHoveredProject(project);
    };

    const handleMarkerLeave = () => {
        hoverTimeoutRef.current = setTimeout(() => {
            setHoveredProject(null);
        }, 300); // Delay to allow moving into popup
    };

    // Keep popup open when hovering over it
    const handlePopupEnter = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
    };

    const handlePopupLeave = () => {
        hoverTimeoutRef.current = setTimeout(() => {
            setHoveredProject(null);
        }, 300);
    };

    return (
        <div className="min-h-screen bg-background flex flex-col relative overflow-hidden selection:bg-secondary selection:text-white pt-24 md:pt-32 pb-12 md:pb-20">
            {/* Background Effects */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-secondary/5 blur-[120px] rounded-full mix-blend-screen" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-900/10 blur-[100px] rounded-full mix-blend-screen" />
            </div>

            <Container className="flex-1 flex flex-col relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1 }}
                    className="text-center mb-10 md:mb-20"
                >
                    <div className="flex items-center justify-center gap-4 mb-4 md:mb-6">
                        <span className="h-px w-8 md:w-12 bg-secondary"></span>
                        <span className="text-secondary font-bold tracking-[0.2em] uppercase text-xs md:text-sm">Find Us</span>
                        <span className="h-px w-8 md:w-12 bg-secondary"></span>
                    </div>
                    {/* <h1 className="text-4xl md:text-7xl lg:text-8xl font-serif font-bold text-primary mb-6 md:mb-8 leading-tight drop-shadow-sm ">
                                            Building Heritage, <br />
                                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-primary italic">Design Legacy</span>
                                        </h1>
                                        <p className="text-base md:text-xl text-primary/80 max-w-2xl mx-auto font-medium leading-relaxed px-4">
                                            Crafting sustainable communities and premium living spaces that stand the test of time.
                                        </p> */}
                    <h1 className="text-4xl text-center md:text-7xl lg:text-8xl font-serif text-primary mb-6 md:mb-8 leading-tight font-bold">
                        Discover Our <br />
                        <span className="">Premium Locations</span>
                    </h1>
                    <p className="text-secondary text-base md:text-lg leading-relaxed max-w-2xl mx-auto font-medium">
                        Explore our premium developments across the city. Each location is strategically chosen to offer the best connectivity and lifestyle.
                    </p>
                </motion.div>

                <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 flex-1 min-h-[600px]">
                    {/* Left: Project List */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="w-full lg:w-1/3 flex flex-col gap-6"
                    >
                        {/* Search Bar */}
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <Search className="text-gray-500 group-focus-within:text-secondary transition-colors" size={20} />
                            </div>
                            <input
                                type="text"
                                placeholder="Search projects..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full bg-white border border-primary/10 rounded-full py-4 pl-12 pr-4 text-primary placeholder-light/70 focus:outline-none focus:border-secondary/50 focus:bg-white transition-all font-light shadow-sm"
                            />
                        </div>

                        {/* Projects Scroll List */}
                        <div className="flex-1 overflow-y-auto pr-2 space-y-4 max-h-[600px] custom-scrollbar">
                            {loading ? (
                                <div className="text-center py-10 text-gray-500">
                                    <div className="w-8 h-8 border-2 border-secondary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                                    Loading projects...
                                </div>
                            ) : validProjects.length > 0 ? (
                                validProjects.map((project) => (
                                    <motion.div
                                        key={project._id}
                                        onClick={() => handleProjectClick(project)}
                                        onMouseEnter={() => {
                                            handleProjectClick(project);
                                            handleMarkerEnter(project);
                                        }}
                                        onMouseLeave={handleMarkerLeave}
                                        className={`p-5 rounded-2xl cursor-pointer transition-all duration-300 border ${selectedProject?._id === project._id
                                            ? 'bg-secondary/10 border-secondary/50 shadow-[0_0_20px_rgba(198,168,124,0.1)]'
                                            : 'bg-white border-primary/5 hover:bg-white hover:shadow-lg hover:border-primary/10'
                                            }`}
                                    >
                                        <div className="flex justify-between items-start mb-2">
                                            <h3 className={`font-serif font-bold text-xl ${selectedProject?._id === project._id ? 'text-secondary' : 'text-primary'}`}>
                                                {project.title}
                                            </h3>
                                            {project.status && (
                                                <span className="text-[10px] px-2 py-1 bg-white/10 rounded-full text-gray-800 uppercase tracking-wider">
                                                    {project.status}
                                                </span>
                                            )}
                                        </div>
                                        {/* Project Image in List */}
                                        <div className="w-full h-32 mb-4 rounded-lg overflow-hidden relative">
                                            <img
                                                src={project.images && project.images.length > 0 ? project.images[0].url : "https://via.placeholder.com/400x300?text=No+Image"}
                                                alt={project.title}
                                                className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                                        </div>
                                        <div className="flex items-start gap-2 text-secondary/80 text-sm mb-4">
                                            <MapPin size={16} className="text-secondary shrink-0 mt-0.5" />
                                            <span className="line-clamp-2">{project.officeAddress || "Address not available"}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <button
                                                className="text-xs uppercase font-bold tracking-wider text-secondary flex items-center gap-1 hover:text-primary transition-colors"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/project/${project.sku}`);
                                                }}
                                            >
                                                View Details <ExternalLink size={12} />
                                            </button>
                                        </div>
                                    </motion.div>
                                ))
                            ) : (
                                <div className="text-center py-10 text-gray-500">
                                    <MapPin size={40} className="mx-auto mb-3 opacity-20" />
                                    No projects found matching your search.
                                </div>
                            )}
                        </div>
                    </motion.div>

                    {/* Right: Map - Redesigned */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="w-full lg:w-2/3 h-[500px] lg:h-auto min-h-[500px] rounded-3xl overflow-hidden relative group"
                    >
                        {/* Premium Border/Glow Effect */}
                        <div className="absolute inset-0 rounded-3xl z-20 pointer-events-none border border-secondary/30 shadow-[inset_0_0_20px_rgba(198,168,124,0.1)]"></div>

                        {/* Glass Overlay for depth */}
                        <div className="absolute inset-0 bg-gradient-to-tr from-black/20 via-transparent to-black/20 pointer-events-none z-10"></div>

                        {isLoaded ? (
                            <GoogleMap
                                mapContainerStyle={MAP_CONTAINER_STYLE}
                                center={DEFAULT_CENTER}
                                zoom={12}
                                onLoad={onLoad}
                                onUnmount={onUnmount}
                                options={{
                                    streetViewControl: false,
                                    mapTypeControl: false,
                                    fullscreenControl: true,
                                    zoomControl: true,
                                    zoomControlOptions: {
                                        position: window.google?.maps?.ControlPosition?.RIGHT_CENTER
                                    }
                                }}
                            >
                                {validProjects.map((project) => (
                                    <Marker
                                        key={project._id}
                                        position={{ lat: project.location.lat, lng: project.location.lng }}
                                        icon={hoveredProject?._id === project._id ? HOVERED_MARKER_ICON : CUSTOM_MARKER_ICON}
                                        onMouseOver={() => handleMarkerEnter(project)}
                                        onMouseOut={handleMarkerLeave}
                                        onClick={() => handleProjectClick(project)}
                                        animation={window.google?.maps?.Animation?.DROP}
                                    />
                                ))}

                                {/* Floating Popup via OverlayView */}
                                <AnimatePresence>
                                    {hoveredProject && hoveredProject.location && (
                                        <OverlayPopup
                                            key="popup"
                                            project={hoveredProject}
                                            map={map}
                                            onEnter={handlePopupEnter}
                                            onLeave={handlePopupLeave}
                                            navigate={navigate}
                                        />
                                    )}
                                </AnimatePresence>
                            </GoogleMap>
                        ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center bg-white/5 text-gray-400">
                                {loadError ? (
                                    <div className="text-center px-6">
                                        <p className="mb-2 text-red-400">Map failed to load.</p>
                                        <p className="text-sm">Please check your internet connection or API key.</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="w-10 h-10 border-2 border-secondary border-t-transparent rounded-full animate-spin mb-4"></div>
                                        <p className="text-sm tracking-wider font-light text-primary">LOADING MAP</p>
                                    </>
                                )}
                            </div>
                        )}
                    </motion.div>
                </div>
            </Container>
        </div>
    );
};

// Component to handle OverlayView and Positioning
const OverlayPopup = ({ project, map, onEnter, onLeave, navigate }) => {
    const [isAbove, setIsAbove] = useState(true);
    const overlayRef = useRef(null);

    const onOverlayLoad = (overlay) => {
        overlayRef.current = overlay;
        // Trigger initial position check
        checkPosition();
    };

    const checkPosition = () => {
        if (overlayRef.current && project.location) {
            try {
                // Ensure getProjection is available
                const projection = overlayRef.current.getProjection();
                if (projection) {
                    const point = projection.fromLatLngToContainerPixel(
                        new window.google.maps.LatLng(project.location.lat, project.location.lng)
                    );

                    // If pixel.y is small (near top of container), show below the marker.
                    // Threshold: Card height (approx 300px) + header padding (approx 50-100px)
                    if (point && point.y < 300) {
                        setIsAbove(false);
                    } else {
                        setIsAbove(true);
                    }
                }
            } catch (error) {
                console.error("Error calculating popup position:", error);
            }
        }
    };

    // Re-check position when project changes
    useEffect(() => {
        // Use a small timeout to ensure projection is ready if switching quickly
        const timer = setTimeout(checkPosition, 0);
        return () => clearTimeout(timer);
    }, [project]);

    return (
        <OverlayView
            position={{ lat: project.location.lat, lng: project.location.lng }}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            getPixelPositionOffset={(width, height) => ({
                x: -(width / 2),
                y: 0
            })}
            onLoad={onOverlayLoad}
        >
            <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ duration: 0.3, type: "spring", stiffness: 300, damping: 25 }}
                className={`absolute left-0 w-[280px] pointer-events-auto cursor-default z-50 ${isAbove ? 'bottom-[45px]' : 'top-[20px]'
                    }`}
                onMouseEnter={onEnter}
                onMouseLeave={onLeave}
            >
                <div className="bg-white backdrop-blur-xl border border-secondary/30 rounded-2xl overflow-hidden shadow-2xl relative group/card">

                    {/* Arrow/Pointer */}
                    <div className={`absolute left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-r border-b border-secondary/30 rotate-45 transform ${isAbove
                        ? '-bottom-2 border-l-0 border-t-0'
                        : '-top-2 border-r-0 border-b-0 border-l border-t'
                        }`}></div>

                    {/* Image */}
                    <div className="h-32 w-full relative overflow-hidden">
                        <img
                            src={project.images?.[0]?.url || "https://via.placeholder.com/300"}
                            alt={project.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover/card:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0f1115] to-transparent opacity-90"></div>
                        <span className={`absolute  top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded shadow-sm ${project.status === 'Completed' ? 'bg-green-500/90 text-white' : 'bg-secondary/90 text-black'
                            }`}>
                            {project.status}
                        </span>
                    </div>

                    {/* Content */}
                    <div className="p-4 pt-1 relative z-10">
                        <h3 className="text-lg font-serif text-primary font-medium truncate mb-1">{project.title}</h3>
                        {/* <div className="flex items-start gap-1.5 text-xs text-gray-400 mb-4 h-8 overflow-hidden">
                            <MapPin size={12} className="text-secondary shrink-0 mt-0.5" />
                            <span className="line-clamp-2 leading-relaxed text-secondary mt-1">{project.officeAddress}</span>
                        </div> */}

                        <div className="flex gap-2">
                            <a
                                href={`https://www.google.com/maps?q=${project.location?.lat},${project.location?.lng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 py-1.5 flex items-center justify-center gap-1.5 bg-primary/5 hover:bg-primary/10 border border-primary/10 rounded-lg text-xs text-primary transition-colors"
                            >
                                <Navigation size={12} className="text-secondary" /> Direction
                            </a>
                            <button
                                onClick={() => navigate(`/project/${project.sku}`)}
                                className="flex-1 py-1.5 bg-secondary hover:bg-white text-black text-xs font-bold rounded-lg transition-colors shadow-lg shadow-secondary/20"
                            >
                                View Details
                            </button>
                        </div>
                    </div>
                </div>
            </motion.div>
        </OverlayView>
    );
};

export default LocationPage;
